import React, { useEffect, useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { eventAPI, attendeeAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  QrCode,
  Camera,
  Search,
  CheckCircle,
  Mail,
  Loader2,
  X,
  User,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Html5Qrcode } from 'html5-qrcode';

export default function CheckIn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedEventId = searchParams.get('event');
  
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(preselectedEventId || '');
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [scanning, setScanning] = useState(false);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' = rear, 'user' = front
  const [checkingIn, setCheckingIn] = useState(null);
  const [sendingCert, setSendingCert] = useState(null);
  const [foundAttendee, setFoundAttendee] = useState(null);
  
  const scannerRef = useRef(null);
  // Monotonic op counter — every start/switch grabs the current value+1;
  // stop just increments it. If a saved value != current, that operation
  // has been superseded (e.g. user clicked Stop mid-Switch) and must abort.
  const opIdRef = useRef(0);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchAttendees();
    }
  }, [selectedEvent]);

  useEffect(() => {
    return () => {
      // Cleanup scanner on unmount
      const inst = scannerRef.current;
      if (inst) {
        inst.stop().catch(() => {}).finally(() => {
          try { inst.clear(); } catch (_) { /* noop */ }
        });
      }
    };
  }, []);

  // Silence dev-only "The play() request was interrupted because the media was
  // removed from the document" pageerrors that html5-qrcode triggers on its
  // internal video element during switch/stop transitions. These are harmless
  // Promise rejections from the library, but React's dev-server error overlay
  // iframe reacts to them and covers the entire viewport with an invisible
  // pointer-trapping frame — blocking taps on Switch/Stop buttons. Real
  // production builds don't have this overlay, but this makes the app robust
  // regardless.
  useEffect(() => {
    const isBenignPlayError = (msg) => {
      const s = String(msg || '');
      return (
        s.includes('play()') ||
        s.includes('media was removed from the document') ||
        s.includes('AbortError') ||
        s.includes('The operation was aborted') ||
        s.includes('onabort()')
      );
    };
    const rejectionHandler = (event) => {
      const msg = event?.reason?.message || event?.reason || '';
      if (isBenignPlayError(msg)) event.preventDefault();
    };
    const errorHandler = (event) => {
      const msg = event?.message || event?.error?.message || '';
      if (isBenignPlayError(msg)) event.preventDefault();
    };
    window.addEventListener('unhandledrejection', rejectionHandler);
    window.addEventListener('error', errorHandler);
    return () => {
      window.removeEventListener('unhandledrejection', rejectionHandler);
      window.removeEventListener('error', errorHandler);
    };
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await eventAPI.list();
      setEvents(response.data);
      if (preselectedEventId && response.data.find(e => e.id === preselectedEventId)) {
        setSelectedEvent(preselectedEventId);
      }
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async () => {
    if (!selectedEvent) return;
    try {
      const response = await attendeeAPI.list(selectedEvent);
      setAttendees(response.data);
    } catch (error) {
      toast.error('Failed to load attendees');
    }
  };

  const startScanner = async (preferredFacing) => {
    const myOp = ++opIdRef.current;
    setScanning(true);
    // Wait for the qr-reader div to actually mount before initializing
    await new Promise((r) => setTimeout(r, 100));
    if (myOp !== opIdRef.current) return; // Stop was clicked

    const container = document.getElementById('qr-reader');
    if (!container) {
      toast.error('Scanner container not ready. Please try again.');
      setScanning(false);
      return;
    }

    const config = { fps: 10, qrbox: { width: 250, height: 250 } };

    // Fresh Html5Qrcode instance per start() attempt: reusing the same instance
    // after a failed start() causes "Cannot transition to a new state" errors.
    const tryStart = async (mode) => {
      const inst = new Html5Qrcode('qr-reader');
      try {
        await inst.start(
          { facingMode: mode },       // plain string form required by html5-qrcode v2.3.8
          config,
          onScanSuccess,
          onScanError,
        );
      } catch (e) {
        try { inst.clear(); } catch (_) { /* noop */ }
        throw e;
      }
      // Post-start cancellation check: user might have clicked Stop while
      // the browser was still opening the camera stream.
      if (myOp !== opIdRef.current) {
        try { await inst.stop(); } catch (_) { /* noop */ }
        try { inst.clear(); } catch (_) { /* noop */ }
        return false;
      }
      scannerRef.current = inst;
      setFacingMode(mode);
      return true;
    };

    const primary = preferredFacing || 'environment';
    const fallback = primary === 'environment' ? 'user' : 'environment';

    try {
      const ok = await tryStart(primary);
      if (!ok && myOp !== opIdRef.current) return;
    } catch (err) {
      if (myOp !== opIdRef.current) return;
      // Rear camera not available (e.g., some laptops) — try the other one
      try {
        const ok = await tryStart(fallback);
        if (!ok && myOp !== opIdRef.current) return;
      } catch (err2) {
        if (myOp !== opIdRef.current) return;
        console.error('Camera start failed:', err, err2);
        const combinedMsg = String(err2?.message || err2 || err?.message || err || '').toLowerCase();
        if (
          combinedMsg.includes('permission') ||
          combinedMsg.includes('denied') ||
          combinedMsg.includes('notallowed')
        ) {
          toast.error(
            'Camera permission denied. On iPhone: Settings → Safari → Camera → Allow, then reload.',
            { duration: 8000 },
          );
        } else {
          toast.error(
            'Could not start camera. Try the "Switch camera" button, or use search by name/email below.',
            { duration: 6000 },
          );
        }
        setScanning(false);
      }
    }
  };

  const _forceKillScanner = () => {
    // Immediately release any live media track and wipe the scanner container.
    // This is a belt-and-braces guarantee that Stop always ends the camera
    // stream regardless of what Html5Qrcode's async stop()/clear() decides to
    // do (it can hang or fail silently when the video is mid-transition).
    const container = document.getElementById('qr-reader');
    if (container) {
      container.querySelectorAll('video').forEach((v) => {
        try {
          const stream = v.srcObject;
          if (stream && typeof stream.getTracks === 'function') {
            stream.getTracks().forEach((t) => { try { t.stop(); } catch (_) { /* noop */ } });
          }
          v.pause();
          v.removeAttribute('src');
          v.srcObject = null;
        } catch (_) { /* noop */ }
      });
      try { container.innerHTML = ''; } catch (_) { /* noop */ }
    }
  };

  const stopScanner = async () => {
    // Bump op id FIRST so any in-flight start()/switch() aborts before
    // undoing our UI reset.
    opIdRef.current += 1;
    const inst = scannerRef.current;
    scannerRef.current = null;
    _forceKillScanner();
    // flushSync forces React to commit this state change synchronously so
    // the #qr-reader div is torn down BEFORE we begin any Html5Qrcode.stop()
    // cleanup — this avoids "media removed from document" pageerrors and
    // guarantees the UI transitions back to the Start button immediately.
    flushSync(() => {
      setScanning(false);
    });
    if (inst) {
      Promise.race([
        inst.stop().catch(() => {}),
        new Promise((r) => setTimeout(r, 2000)),
      ]).finally(() => {
        try { inst.clear(); } catch (_) { /* noop */ }
      });
    }
  };

  const switchCamera = async () => {
    const myOp = ++opIdRef.current;
    const nextFacing = facingMode === 'environment' ? 'user' : 'environment';
    const inst = scannerRef.current;
    scannerRef.current = null;
    if (inst) {
      await Promise.race([
        inst.stop().catch(() => {}),
        new Promise((r) => setTimeout(r, 1500)),
      ]);
      try { inst.clear(); } catch (_) { /* noop */ }
    }
    if (myOp !== opIdRef.current) {
      // Stop was clicked during our old-scanner shutdown — surrender cleanly
      flushSync(() => { setScanning(false); });
      _forceKillScanner();
      return;
    }
    await new Promise((r) => setTimeout(r, 200));
    if (myOp !== opIdRef.current) {
      flushSync(() => { setScanning(false); });
      _forceKillScanner();
      return;
    }
    await startScanner(nextFacing);
  };

  const onScanSuccess = async (decodedText) => {
    // The new personal check-in QR contains the attendee ID (UUID v4).
    // Legacy QRs may contain an email or a registration URL with ?email=.
    let scannedValue = (decodedText || '').trim();

    // If it's a URL, attempt to extract email param
    let candidateEmail = scannedValue;
    if (scannedValue.includes('http')) {
      try {
        const url = new URL(scannedValue);
        candidateEmail = url.searchParams.get('email') || scannedValue;
      } catch {
        // not a URL, ignore
      }
    }

    // 1) Try exact attendee-ID match (new personal QR)
    let attendee = attendees.find((a) => a.id === scannedValue);
    // 2) Fallback: legacy email-based QR
    if (!attendee) {
      attendee = attendees.find((a) => a.email.toLowerCase() === candidateEmail.toLowerCase());
    }

    if (attendee) {
      setFoundAttendee(attendee);
      await stopScanner();
      toast.success(`Found: ${attendee.name}`);
    } else {
      toast.error('Attendee not found for this event');
    }
  };

  const onScanError = (error) => {
    // Library invokes this on EVERY frame without a code. Silence those.
    const msg = String(error?.message || error || '');
    if (msg && !msg.includes('No MultiFormat Readers') && !msg.includes('No barcode') && !msg.includes('NotFoundException')) {
      console.debug('Scan tick:', msg);
    }
  };

  const handleSearchByEmail = () => {
    if (!searchEmail.trim()) return;
    
    const attendee = attendees.find(a => 
      a.email.toLowerCase().includes(searchEmail.toLowerCase()) ||
      a.name.toLowerCase().includes(searchEmail.toLowerCase())
    );
    
    if (attendee) {
      setFoundAttendee(attendee);
      toast.success(`Found: ${attendee.name}`);
    } else {
      toast.error('Attendee not found');
    }
  };

  const handleCheckIn = async () => {
    if (!foundAttendee) return;
    
    setCheckingIn(foundAttendee.id);
    try {
      const res = await attendeeAPI.checkIn(foundAttendee.id, {});
      const updated = res?.data || {};
      if (updated.certificate_sent) {
        toast.success(`Checked in! Certificate emailed to ${foundAttendee.email}`);
      } else {
        toast.success('Checked in successfully!');
      }
      fetchAttendees();
      setFoundAttendee({
        ...foundAttendee,
        checked_in: true,
        arrival_time: updated.arrival_time || new Date().toISOString(),
        certificate_sent: updated.certificate_sent || foundAttendee.certificate_sent,
      });
    } catch (error) {
      toast.error('Failed to check in');
    } finally {
      setCheckingIn(null);
    }
  };

  const handleSendCertificate = async () => {
    if (!foundAttendee) return;
    
    setSendingCert(foundAttendee.id);
    try {
      await attendeeAPI.sendCertificate(foundAttendee.id);
      toast.success('Certificate sent!');
      fetchAttendees();
      setFoundAttendee({ ...foundAttendee, certificate_sent: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send certificate');
    } finally {
      setSendingCert(null);
    }
  };

  const clearFoundAttendee = () => {
    setFoundAttendee(null);
    setSearchEmail('');
  };

  return (
    <Layout activeNav="checkin">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Check-in Attendees</h1>
          <p className="text-muted-foreground">Scan QR codes or search by email to check in attendees</p>
        </div>

        {/* Event Selector */}
        <Card className="mb-6 border-border">
          <CardHeader>
            <CardTitle className="text-lg">Select Event</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedEvent} onValueChange={setSelectedEvent}>
              <SelectTrigger className="h-11" data-testid="event-selector">
                <SelectValue placeholder="Choose an event" />
              </SelectTrigger>
              <SelectContent>
                {events.map((event) => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.title} ({event.date})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {selectedEvent && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Scanner / Search */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Check-in
                </CardTitle>
                <CardDescription>
                  Scan QR code or search by name/email
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!scanning ? (
                  <div className="space-y-4">
                    <Button 
                      onClick={() => startScanner()}
                      className="w-full h-12 gap-2"
                      data-testid="start-scanner-btn"
                    >
                      <Camera className="w-5 h-5" />
                      Start QR Scanner
                    </Button>
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">or search manually</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Input
                        placeholder="Search by name or email..."
                        value={searchEmail}
                        onChange={(e) => setSearchEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearchByEmail()}
                        className="h-11"
                        data-testid="search-attendee-input"
                      />
                      <Button 
                        onClick={handleSearchByEmail}
                        variant="outline"
                        className="h-11"
                        data-testid="search-attendee-btn"
                      >
                        <Search className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div id="qr-reader" className="rounded-lg overflow-hidden" />
                    <p className="text-xs text-muted-foreground text-center">
                      Using {facingMode === 'environment' ? 'rear' : 'front'} camera — hold the attendee&apos;s QR steady
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        onClick={switchCamera}
                        variant="outline"
                        className="gap-2"
                        data-testid="switch-camera-btn"
                      >
                        <Camera className="w-4 h-4" />
                        Switch camera
                      </Button>
                      <Button 
                        onClick={stopScanner}
                        variant="outline"
                        className="gap-2"
                        data-testid="stop-scanner-btn"
                      >
                        <X className="w-4 h-4" />
                        Stop Scanner
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Found Attendee / Actions */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Attendee Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                {foundAttendee ? (
                  <div className="space-y-4" data-testid="found-attendee-details">
                    <div className="p-4 rounded-lg bg-muted">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-foreground text-lg">{foundAttendee.name}</h3>
                          <p className="text-sm text-muted-foreground">{foundAttendee.email}</p>
                          {foundAttendee.provider_number && (
                            <p className="text-sm text-muted-foreground">Provider: {foundAttendee.provider_number}</p>
                          )}
                        </div>
                        <Button variant="ghost" size="icon" onClick={clearFoundAttendee}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-border space-y-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${foundAttendee.checked_in ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                          <span className="text-sm">
                            {foundAttendee.checked_in ? 'Checked in' : 'Not checked in'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${foundAttendee.certificate_sent ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                          <span className="text-sm">
                            {foundAttendee.certificate_sent ? 'Certificate sent' : 'Certificate not sent'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {!foundAttendee.checked_in && (
                        <Button 
                          onClick={handleCheckIn}
                          className="w-full h-12 gap-2"
                          disabled={checkingIn === foundAttendee.id}
                          data-testid="checkin-found-attendee-btn"
                        >
                          {checkingIn === foundAttendee.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <CheckCircle className="w-5 h-5" />
                          )}
                          Check In
                        </Button>
                      )}
                      
                      {foundAttendee.checked_in && !foundAttendee.certificate_sent && (
                        <Button 
                          onClick={handleSendCertificate}
                          className="w-full h-12 gap-2"
                          disabled={sendingCert === foundAttendee.id}
                          data-testid="send-cert-found-attendee-btn"
                        >
                          {sendingCert === foundAttendee.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Mail className="w-5 h-5" />
                          )}
                          Send Certificate
                        </Button>
                      )}
                      
                      {foundAttendee.checked_in && foundAttendee.certificate_sent && (
                        <div className="flex items-center justify-center gap-2 p-4 bg-emerald-50 rounded-lg text-emerald-700">
                          <CheckCircle className="w-5 h-5" />
                          <span>All done!</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <AlertCircle className="w-12 h-12 mb-4 opacity-50" />
                    <p>No attendee selected</p>
                    <p className="text-sm mt-1">Scan a QR code or search to find an attendee</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Check-ins */}
        {selectedEvent && attendees.filter(a => a.checked_in).length > 0 && (
          <Card className="mt-6 border-border">
            <CardHeader>
              <CardTitle className="text-lg">Recent Check-ins</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {attendees
                  .filter(a => a.checked_in)
                  .sort((a, b) => new Date(b.arrival_time) - new Date(a.arrival_time))
                  .slice(0, 5)
                  .map((attendee) => (
                    <div 
                      key={attendee.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                    >
                      <div>
                        <p className="font-medium text-foreground">{attendee.name}</p>
                        <p className="text-sm text-muted-foreground">{attendee.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {attendee.certificate_sent ? (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                            Cert Sent
                          </span>
                        ) : (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
                            Cert Pending
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
